'use client';

import { ChevronDown, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';

import { useVenueLabel } from './use-venue-picker';
import { useWebVenuePicker } from './web-venue-picker';

/**
 * "Venue: Riyadh · Change" row at the top of a venue-scoped catalog (packages,
 * memberships, fixed-slot packages, coaches) on the web. It tells the visitor
 * which venue the list is for and re-opens the venue picker.
 *
 * Renders nothing in the native shell (the top-bar chip covers it there) and
 * for single-venue tenants.
 */
export function VenueBar({ className }: { className?: string }) {
  const picker = useWebVenuePicker();
  const t = useNativeTokens();
  const { t: tr } = useTranslation('bookings');
  const labelFor = useVenueLabel();

  if (!picker) return null;

  const label = picker.activeVenue ? labelFor(picker.activeVenue) : null;

  return (
    <button
      type="button"
      data-test="venue-bar"
      onClick={picker.open}
      aria-haspopup="dialog"
      className={cn(
        'flex w-full items-center gap-3 text-start transition-opacity hover:opacity-90 active:opacity-70',
        className,
      )}
      style={{
        padding: '10px 14px',
        background: t.surface,
        border: `1px solid ${t.border}`,
        borderRadius: t.radius,
        color: t.foreground,
      }}
    >
      <MapPin
        size={16}
        strokeWidth={1.5}
        style={{ color: t.muted, flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: t.fontMono,
            fontSize: 9,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: t.muted,
          }}
        >
          {tr('venueSwitcher.eyebrow', { defaultValue: 'Venue' })}
        </div>
        <div
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 15,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label
            ? [label.name, label.city].filter(Boolean).join(' · ')
            : tr('venueSwitcher.chooseVenue', {
                defaultValue: 'Choose a venue',
              })}
        </div>
      </div>
      <span
        className="flex shrink-0 items-center gap-1"
        style={{ color: t.accent, fontSize: 13, fontWeight: 500 }}
      >
        {label
          ? tr('venueSwitcher.change', { defaultValue: 'Change' })
          : tr('venueSwitcher.choose', { defaultValue: 'Choose' })}
        <ChevronDown size={14} strokeWidth={1.5} />
      </span>
    </button>
  );
}
